import React, { useState, useEffect } from 'react';
import { userService, UserProfile, Empresa } from '../services/api';
import ChangePasswordModal from './ChangePasswordModal';
import CompanyManagement from './CompanyManagement';
import UserManagement from './UserManagement';



const Configuracoes: React.FC = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentView, setCurrentView] = useState<'main' | 'companies' | 'users'>('main');
  const [selectedCompany, setSelectedCompany] = useState<Empresa | null>(null);

  useEffect(() => {
    loadUserProfile();
  }, []);

  const handlePasswordChangeSuccess = () => {
    setSuccessMessage('Senha alterada com sucesso!');
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const profileData = await userService.getProfile();
      setUserProfile(profileData);
    } catch (err: any) {
      console.error('Erro ao carregar perfil do usuário:', err);
      setError('Erro ao carregar informações do usuário. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
                <div className="text-center">
                  <div className="spinner-border text-primary mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p className="text-muted">Carregando configurações...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="alert alert-danger" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
                <button 
                  className="btn btn-outline-danger btn-sm ms-3" 
                  onClick={loadUserProfile}
                >
                  <i className="fas fa-redo me-1"></i>
                  Tentar Novamente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="main-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="alert alert-warning" role="alert">
                <i className="fas fa-exclamation-triangle me-2"></i>
                Não foi possível carregar as informações do usuário.
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Se for administrador, mostrar interface de administração
  if (userProfile.administrador) {
    if (currentView === 'companies') {
      return (
        <div className="main-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <CompanyManagement 
                  onSelectCompany={(company) => {
                    setSelectedCompany(company);
                    setCurrentView('users');
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (currentView === 'users' && selectedCompany) {
      return (
        <div className="main-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                <UserManagement 
                  company={selectedCompany}
                  onBack={() => {
                    setCurrentView('companies');
                    setSelectedCompany(null);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Tela principal do administrador
    return (
      <div className="main-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h2>
                  <i className="fas fa-cog me-2"></i>
                  Configurações - Administrador
                </h2>
              </div>
              
              <div className="row">
                {/* Card de Informações do Admin */}
                <div className="col-lg-4 mb-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-user-shield me-2"></i>
                        Meu Perfil
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="text-center mb-3">
                        <div 
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mx-auto mb-3"
                          style={{ width: '60px', height: '60px' }}
                        >
                          <i className="fas fa-user" style={{ fontSize: '1.5rem' }}></i>
                        </div>
                        <h6 className="fw-bold">{userProfile.nome}</h6>
                        <p className="text-muted mb-0">{userProfile.email}</p>
                        <span className="badge bg-purple text-white mt-2">
                          <i className="fas fa-crown me-1"></i>
                          Administrador
                        </span>
                      </div>
                      
                      <div className="d-grid">
                        <button
                          className="btn btn-outline-primary"
                          onClick={() => setShowPasswordModal(true)}
                        >
                          <i className="fas fa-key me-2"></i>
                          Alterar Senha
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card de Gerenciamento de Empresas */}
                <div className="col-lg-4 mb-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-success text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-building me-2"></i>
                        Empresas
                      </h5>
                    </div>
                    <div className="card-body d-flex flex-column">
                      <div className="text-center mb-3">
                        <i className="fas fa-building text-success mb-3" style={{ fontSize: '3rem' }}></i>
                        <h6>Gerenciar Empresas</h6>
                        <p className="text-muted small mb-0">
                          Cadastre, edite e gerencie todas as empresas do sistema
                        </p>
                      </div>
                      
                      <div className="mt-auto">
                        <div className="d-grid">
                          <button
                            className="btn btn-success"
                            onClick={() => setCurrentView('companies')}
                          >
                            <i className="fas fa-cogs me-2"></i>
                            Gerenciar Empresas
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card de Estatísticas */}
                <div className="col-lg-4 mb-4">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-header bg-info text-white">
                      <h5 className="mb-0">
                        <i className="fas fa-chart-bar me-2"></i>
                        Sistema
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="text-center mb-3">
                        <i className="fas fa-server text-info mb-3" style={{ fontSize: '3rem' }}></i>
                        <h6>Informações do Sistema</h6>
                        <p className="text-muted small mb-0">
                          Visualize estatísticas e informações gerais
                        </p>
                      </div>
                      
                      <div className="row text-center">
                        <div className="col-6">
                          <div className="border-end">
                            <div className="fw-bold text-info" style={{ fontSize: '1.2rem' }}>∞</div>
                            <small className="text-muted">Empresas</small>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="fw-bold text-info" style={{ fontSize: '1.2rem' }}>∞</div>
                          <small className="text-muted">Usuários</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {successMessage && (
          <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1050 }}>
            <div className="alert alert-success alert-dismissible fade show" role="alert">
              <i className="fas fa-check-circle me-2"></i>
              {successMessage}
              <button
                type="button"
                className="btn-close"
                onClick={() => setSuccessMessage('')}
              ></button>
            </div>
          </div>
        )}
        
        <ChangePasswordModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          onSuccess={handlePasswordChangeSuccess}
        />
      </div>
    );
  }

  // Tela para usuários não administradores
  return (
    <div className="main-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2>
                <i className="fas fa-cog me-2"></i>
                Configurações
              </h2>
            </div>
            
            <div className="row justify-content-center">
              <div className="col-lg-8 col-xl-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                      <i className="fas fa-user me-2"></i>
                      Informações do Usuário
                    </h5>
                  </div>
                  <div className="card-body p-4">
                    {/* Informações da Empresa */}
                    <div className="mb-4">
                      <div className="d-flex align-items-center mb-3">
                        <div 
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                          style={{ width: '50px', height: '50px' }}
                        >
                          <i className="fas fa-building" style={{ fontSize: '1.2rem' }}></i>
                        </div>
                        <div>
                          <h4 className="mb-1 text-primary fw-bold">{userProfile.razao}</h4>
                        </div>
                      </div>
                      
                      <div className="row g-3">
                        <div className="col-12">
                          <div className="d-flex align-items-center p-3 bg-light rounded">
                            <div className="me-3">
                              <i className="fas fa-id-card text-primary" style={{ fontSize: '1.1rem' }}></i>
                            </div>
                            <div className="flex-grow-1">
                              <label className="form-label mb-1 fw-bold text-muted" style={{ fontSize: '0.85rem' }}>CNPJ</label>
                              <div className="fw-bold">{userProfile.cnpj}</div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="col-12">
                           <div className={`d-flex align-items-center p-3 rounded ${
                             new Date(userProfile.vencimento) < new Date() ? 'bg-danger text-white' : 'bg-light'
                           }`}>
                             <div className="me-3">
                               <i className={`fas fa-calendar-alt ${
                                 new Date(userProfile.vencimento) < new Date() ? 'text-white' : 'text-primary'
                               }`} style={{ fontSize: '1.1rem' }}></i>
                             </div>
                             <div className="flex-grow-1">
                               <label className={`form-label mb-1 fw-bold text-muted ${
                                 new Date(userProfile.vencimento) < new Date() ? 'text-white' : ''
                               }`} style={{ fontSize: '0.85rem' }}>Vencimento da Anuidade</label>
                               <div className="fw-bold">{new Date(userProfile.vencimento).toLocaleDateString('pt-BR')}</div>
                             </div>
                           </div>
                         </div>
                        
                        <div className="col-12">
                          <div className="d-flex align-items-center p-3 bg-light rounded">
                            <div className="me-3">
                              <i className="fas fa-dollar-sign text-primary" style={{ fontSize: '1.1rem' }}></i>
                            </div>
                            <div className="flex-grow-1">
                              <label className="form-label mb-1 fw-bold text-muted" style={{ fontSize: '0.85rem' }}>Valor da Anuidade</label>
                              <div className="fw-bold">R$ {userProfile.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <hr className="my-4" />

                    {/* Informações do Usuário */}
                    <div className="row g-3">
                      <div className="col-12">
                        <div className="d-flex align-items-center p-3 bg-light rounded">
                          <div className="me-3">
                            <i className="fas fa-user text-primary" style={{ fontSize: '1.1rem' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <label className="form-label mb-1 fw-bold text-muted" style={{ fontSize: '0.85rem' }}>Nome</label>
                            <div className="fw-bold">{userProfile.nome}</div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-12">
                        <div className="d-flex align-items-center p-3 bg-light rounded">
                          <div className="me-3">
                            <i className="fas fa-envelope text-primary" style={{ fontSize: '1.1rem' }}></i>
                          </div>
                          <div className="flex-grow-1">
                            <label className="form-label mb-1 fw-bold text-muted" style={{ fontSize: '0.85rem' }}>E-mail</label>
                            <div className="fw-bold">{userProfile.email}</div>
                          </div>
                        </div>
                      </div>
                    </div>


                    
                    <div className="row g-3 mt-3">
                      <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                          <div>
                            <h6 className="mb-1 fw-bold">
                              <i className="fas fa-shield-alt text-primary me-2"></i>
                              Segurança
                            </h6>
                            <small className="text-muted">Altere sua senha de acesso</small>
                          </div>
                          <button
                            className="btn btn-outline-primary"
                            onClick={() => setShowPasswordModal(true)}
                          >
                            <i className="fas fa-key me-2"></i>
                            Alterar Senha
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {successMessage && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1050 }}>
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <i className="fas fa-check-circle me-2"></i>
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage('')}
            ></button>
          </div>
        </div>
      )}
      
      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
    </div>
  );
};

export default Configuracoes;